import { useEffect, useRef } from 'react';
import { useFetcher } from '@remix-run/react';
import type { ProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import { DisplayMode } from '@rocco/types/enum/display-mode';
import { ProductsPageRecentlyViewed } from './ProductsPageRecentlyViewed';
import { CategoryPageRecentlyViewed } from '~/ui-rocco/pages/category-page/CategoryPageRecentlyViewed';
import {
    RECENTLY_VIEWED_ACTION,
    type loader,
} from '~/routes/actions.recently-viewed';

interface RecentlyViewedWrapperProps {
    displayMode: DisplayMode;
}

/**
 * Wrapper component that lazily fetches recently viewed products client-side.
 * This removes the recently viewed fetch from the critical path of the server loader,
 * improving initial page load time (TTFB).
 *
 * Renders ProductsPageRecentlyViewed for desktop, CategoryPageRecentlyViewed for mobile.
 */
export const RecentlyViewedWrapper = ({
    displayMode,
}: RecentlyViewedWrapperProps) => {
    const fetcher = useFetcher<typeof loader>({
        key: 'recently-viewed-products',
    });
    const isDesktop = displayMode === DisplayMode.Desktop;
    const hasFetched = useRef(false);

    useEffect(() => {
        if (fetcher.state === 'idle' && !hasFetched.current) {
            fetcher.load(`${RECENTLY_VIEWED_ACTION}?type=Products`);
            hasFetched.current = true;
        }
    }, [fetcher]);

    const products: ProductTileViewModel[] =
        fetcher.data?.recentlyViewedProducts ?? [];

    // Don't render anything while loading or if no products
    if (fetcher.state === 'loading' || products.length === 0) {
        return null;
    }

    return isDesktop ? (
        <ProductsPageRecentlyViewed items={products} />
    ) : (
        <CategoryPageRecentlyViewed
            displayMode={displayMode}
            items={products}
        />
    );
};
